/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { IEveSoboCheckerData } from './eve-sobo-checker.data';
import { IEveSoboCheckerProps } from './eve-sobo-checker.props.autogenerated';
import { IEligibleCustomerEntity } from '../../actions/ContactActions/DataServiceEntities.g';
import { getEligibleCustomersAsync, saveSessionCustomerAsync } from '../../actions/ContactActions/DataActionExtension.g';
import { computed } from 'mobx';
// import { checkDataAsync } from '../../actions/PeetsExtension/DataActionExtension.g';

export interface IEveSoboCheckerState {
    customersList: IEligibleCustomerEntity[];
    errorMessage: string;
    loaded: boolean;
    showModal: boolean;
}

export interface IEveSoboCheckerViewProps extends IEveSoboCheckerProps<IEveSoboCheckerData> {
    renderLink: React.ReactNode;
    renderTable: React.ReactNode;
    showModal: boolean;
    closeModal: any;
    cartNotEmpty: boolean;
}

/**
 *
 * EveSoboChecker component
 * @extends {React.PureComponent<IEveSoboCheckerProps<IEveSoboCheckerData>, IEveSoboCheckerState>}
 */
class EveSoboChecker extends React.PureComponent<IEveSoboCheckerProps<IEveSoboCheckerData>, IEveSoboCheckerState> {
    constructor(props: IEveSoboCheckerProps<IEveSoboCheckerData>, state: IEveSoboCheckerData) {
        super(props);
        this.state = {
            customersList: [],
            errorMessage: '',
            loaded: false,
            showModal: false
        };
    }

    @computed private get cartNotEmpty(): boolean {
        const {
            data: {
                cart: { result }
            }
        } = this.props;
        return !!result?.cart.CartLines?.length;
    }

    private readonly _loadCustomers = async (): Promise<void> => {
        const {
            context: { actionContext, telemetry },
            resources: { errorMessage }
        } = this.props;
        if (this.state.customersList.length) {
            return;
        }

        await getEligibleCustomersAsync({ callerContext: actionContext })
            .then((data: IEligibleCustomerEntity[]) => {
                if (data && data.length) {
                    this.setState({
                        customersList: data
                    });
                } else {
                    this.setState({
                        customersList: [],
                        errorMessage: errorMessage
                    });
                }
                this.setState({
                    loaded: true,
                    showModal: true
                });
            })
            .catch(error => {
                telemetry.trace(error);
                this.setState({ loaded: true });
            });
    };

    private readonly _closeModal = () => {
        this.setState({
            showModal: false
        });
    };

    private readonly _selectCustomer = async (row: IEligibleCustomerEntity): Promise<void> => {
        if (!row.AccountNumber || !row.ContactPersonID) {
            return;
        }
        const {
            context: { request, actionContext, telemetry },
            resources: { errorMessage }
        } = this.props;
        const cartId = request.cookies.getCartCookie();

        this.setState({ errorMessage: '' });
        await saveSessionCustomerAsync(
            { callerContext: actionContext },
            row.AccountNumber,
            row.ContactPersonID,
            cartId?.split(':')[1] || ''
        )
            .then((data: any) => {
                if (data?.Message === 'Success' || data?.Message === 'Cart exists') {
                    window.location.reload();
                } else {
                    this.setState({ errorMessage: errorMessage });
                }
            })
            .catch(error => {
                telemetry.trace(error);
            });
    };

    private _renderTable = (): React.ReactNode => {
        const customersList = this.state.customersList;
        if (customersList.length) {
            return (
                <table className='eve-sobo-checker__accountTable'>
                    <thead>
                        <tr>
                            <th>Account Number</th>
                            <th>Name</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {customersList.map(c => {
                            let key = 0;
                            return (
                                <tr key={key++}>
                                    <td>{c.AccountNumber}</td>
                                    <td>{c.Name}</td>
                                    <td>
                                        {c.IsSelected ? (
                                            <div className='btn-selected'>Selected</div>
                                        ) : (
                                            <button type='button' className='btn-select' onClick={() => this._selectCustomer(c)}>
                                                Select
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        }
        return <p>{this.props.resources.noUsersText}</p>;
    };

    private _renderLink = (total: number, linkText: string): React.ReactNode => {
        if (total > 0) {
            // let url = '/customerhierarchy/shop_as';
            // let url = `/customerhierarchy/${this.props.config.pageName}`;
            let url = `/${this.props.config.pageType}/${this.props.config.pageName}`;
            if (this.props.config.pageType === '' || this.props.config.pageType === undefined) {
                url = `/${this.props.config.pageName}`;
            }

            if (MsDyn365.isBrowser) {
                // const ocs = window.location.href.indexOf('/ocs') > 1;
                // if (ocs) {
                //     url = `/ocs${url}`;
                // }
                // const scr = window.location.href.indexOf('/scr') > 1;
                // if (scr) {
                //     url = `/scr${url}`;
                // }

                if (document.referrer.indexOf('b2clogin.com') > -1 && window.location.href.toLowerCase().indexOf('shop-as') === -1) {
                    void this._loadCustomers();
                }
            }

            return (
                <div>
                    <a className='eve-sobo-checker__btn-outline' href={url}>
                        <span className='eve-sobo-checker__btn-text'>{linkText}</span>
                    </a>
                </div>
            );
        }
        return <></>;
    };
    public render(): JSX.Element | null {
        if (MsDyn365.isBrowser) {
            if (window.location.href.indexOf('localhost') > -1) {
                setTimeout(() => {
                    const iframe = document.getElementsByTagName('iframe');
                    if (iframe.length) {
                        iframe[0].remove();
                    }
                }, 1000);
            }
        }

        const total = this.props.data.total.result?.Total || 0;
        const linkText = this.props.resources.shopAsLinkText;

        const viewProps = {
            ...this.props,
            cartNotEmpty: this.cartNotEmpty,
            renderTable: this._renderTable(),
            renderLink: this._renderLink(total, linkText),
            showModal: this.state.showModal,
            closeModal: this._closeModal
        };
        return this.props.renderView(viewProps);
    }
}

export default EveSoboChecker;
